import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { UserMenu } from './UserMenu';
import './Header.css';

interface Conversation {
  id: number;
  title: string;
  messages: Array<{ sender: 'user' | 'ai' | 'system'; content: string }>;
  timestamp: Date;
}

interface HeaderProps {
  user: User | null;
  onLoginClick: () => void;
  onSignOut: () => void;
  currentPath?: string;
  showHistory?: boolean;
  onHistoryToggle?: () => void;
  historyCount?: number;
  conversations?: Conversation[];
  onSelectConversation?: (conversationId: number) => void;
  formatTime?: (date: Date) => string;
}

export const Header: React.FC<HeaderProps> = ({ 
  user, 
  onLoginClick, 
  onSignOut, 
  showHistory = false,
  onHistoryToggle,
  historyCount = 0,
  conversations = [],
  onSelectConversation,
  formatTime
}) => {
  const location = useLocation();
  const path = location.pathname;
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const historyRef = useRef<HTMLDivElement>(null);

  const isActive = (routePath: string) => {
    if (routePath === '/' && path === '/') return true;
    if (routePath !== '/' && path.startsWith(routePath)) return true;
    return false;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (historyRef.current && !historyRef.current.contains(event.target as Node)) {
        setIsHistoryOpen(false);
      }
    };
    if (isHistoryOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHistoryOpen]);

  const handleHistoryClick = () => {
    setIsHistoryOpen(!isHistoryOpen);
    if (onHistoryToggle) {
      onHistoryToggle();
    }
  };

  const handleConversationSelect = (conversationId: number) => {
    if (onSelectConversation) {
      onSelectConversation(conversationId);
    }
    setIsHistoryOpen(false);
  };

  const defaultFormatTime = (date: Date): string => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <header className="site-header">
      <div className="header-container">
        <div className="logo-container">
          <Link to="/" className="logo">
            {/* Pinak Logo - Isometric 3D cube with AI center */}
            <svg className="logo-icon" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 4L28 11V21L16 28L4 21V11L16 4Z" fill="currentColor"/>
              <path d="M16 4L28 11L16 18L4 11L16 4Z" fill="currentColor" opacity="0.7"/>
              <path d="M16 18L28 11V21L16 28V18Z" fill="currentColor"/>
              <path d="M16 18L4 11V21L16 28V18Z" fill="currentColor" opacity="0.5"/>
              <circle cx="16" cy="16" r="3" className="logo-center"/>
            </svg>
            <span>Pinak</span>
          </Link>
          <span className="logo-divider"></span>
          <span className="logo-tagline">AI-Powered CAD</span>
        </div>
        <nav>
          <ul className="nav-links">
            <li>
              <Link to="/use-cases" className={isActive('/use-cases') ? 'active' : ''}>
                Use Cases
              </Link>
            </li>
            <li>
              <Link to="/about" className={isActive('/about') ? 'active' : ''}>
                About
              </Link>
            </li>
          </ul>
        </nav>
        <div className="header-actions">
          {showHistory && (
            <div className="header-history" ref={historyRef}>
              <button 
                className={`header-history-button ${isHistoryOpen ? 'active' : ''}`} 
                onClick={handleHistoryClick} 
                id="headerHistoryButton"
              >
                <svg className="header-history-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 1V4M8 4L10 2M8 4L6 2M1 8H4M4 8L2 6M4 8L2 10M15 8H12M12 8L14 6M12 8L14 10M8 15V12M8 12L10 14M8 12L6 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>History</span>
                <span className="history-count">{historyCount || conversations.length}</span>
              </button>
              {isHistoryOpen && (
                <div className="header-history-dropdown">
                  <div className="header-history-dropdown-content">
                    {conversations.length > 0 ? (
                      conversations.map((conv) => (
                        <div
                          key={conv.id}
                          className="header-history-item"
                          onClick={() => handleConversationSelect(conv.id)}
                        >
                          <div className="header-history-item-title">{conv.title}</div>
                          <div className="header-history-item-time">
                            {formatTime ? formatTime(conv.timestamp) : defaultFormatTime(conv.timestamp)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="header-history-empty">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>No history yet</span>
                        <span className="header-history-empty-hint">Your conversations will appear here</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {user ? (
            <UserMenu user={user} onSignOut={onSignOut} />
          ) : (
            <button onClick={onLoginClick} className="btn-primary">
              Get Started
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
